/**
 * The voice-sample dialog behind its screens (ADR-0062): the microphone in a recording session,
 * the machine that decides the phase, the upload, the review's player and the screen kept awake,
 * for one opening of the dialog. The sample lives in the tab's memory until the server stored it
 * — never at rest on the device (ADR-0048). Closing the dialog ends the opening: the microphone
 * is let go, the upload stopped, the player silenced and what was recorded dropped — so closing
 * asks first where that would lose a sample worth keeping.
 *
 * Two modes: `collect` hands the take the operator keeps to whoever opened the dialog and sends
 * nothing (before a subject exists); `upload` stores it with the subject and then shows what the
 * page learns of its job. One instance serves one opening: mount it anew (a key) to open again.
 */
import {
  type Dispatch,
  type RefObject,
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from 'react'
import type { RecordingFileProblem } from '@/components/humint/questioning/recording-types'
import type { CaptureState } from '@/components/humint/types'
import {
  sameQuality,
  type VoiceSampleQuality,
} from '@/components/humint/voice/voice-sample-quality'
import {
  IDLE_VOICE_READOUT,
  type VoicePlayer,
  type VoiceReadout,
  type VoiceSampleActions,
  type VoiceSampleView,
  type VoiceSubject,
} from '@/components/humint/voice/voice-types'
import { MAX_VOICE_SAMPLE_BYTES } from '@/services/api/humint-voice'
import { canRecordHere } from '../recording/browser-recorder'
import { type ReadoutSource, ReadoutStore } from '../recording/readout-store'
import type { RecorderDevice } from '../recording/recorder-controller'
import { useAudioPlayback } from '../recording/useAudioPlayback'
import { useScreenWakeLock } from '../recording/useScreenWakeLock'
import { useObjectUrl } from './useObjectUrl'
import {
  closeAsks,
  hasUnsavedTake,
  heldTake,
  INITIAL_VOICE_FLOW,
  isClosable,
  recordAgainAsks,
  type ScreenArrival,
  screenArrival,
  sendTarget,
  storedCaptureId,
  tapCounts,
  toVoiceSampleView,
  type VoiceFlowEvent,
  type VoiceFlowOffer,
  type VoiceFlowState,
  type VoiceScreen,
  voiceFlowOffers,
  voiceFlowReducer,
  voiceOutcomeOf,
  voiceScreenOf,
} from './voice-sample-flow'
import { openVoiceSampleRecorder } from './voice-sample-recorder'
import { VoiceSampleSession } from './voice-sample-session'
import { takeOfSample, takeOfVoiceFile, type VoiceSampleTake } from './voice-sample-take'
import {
  sendVoiceSampleTake,
  type VoiceSampleTarget,
  type VoiceSendOutcome,
} from './voice-sample-upload'

/** The take the operator keeps is handed back; nothing is sent. */
export interface VoiceCollectMode {
  kind: 'collect'
  /** The take is the caller's now: to hold, and to send once there is a subject. */
  onCollected: (take: VoiceSampleTake) => void
  /**
   * The subject exists already (the enrol sheet after it was created): the caller sends the take
   * the moment it is handed over, and the review says so instead of *saved once the subject is
   * created*.
   */
  sendsAtOnce?: boolean
}

/** The take the operator keeps is stored with the subject. */
export interface VoiceUploadMode extends VoiceSampleTarget {
  kind: 'upload'
  /** Whose voice it is, as the dialog's header names them. */
  subject?: VoiceSubject | null
  /** The server stored the sample as this capture; its voiceprint is still to be computed. */
  onStored?: (captureId: string) => void
  /**
   * What the page knows of a capture's state (its capture-state poll). Asked for the capture
   * this dialog stored, so the dialog can say *stored* or *too little speech* while it is open.
   */
  captureStateOf?: (captureId: string) => CaptureState | null | undefined
  /**
   * What the page knows of a stored sample's quality, from the same poll: the server's verdict
   * once the voiceprint is kept. Asked for the capture this dialog stored, so the dialog can say
   * *excellent*, *usable* or *weak* while it is open.
   */
  captureQualityOf?: (captureId: string) => VoiceSampleQuality | null | undefined
}

export type VoiceSampleFlowMode = VoiceCollectMode | VoiceUploadMode

/** What a story or a test hands in instead of the browser and the server. */
export interface VoiceSampleFlowDeps {
  /** How the microphone is opened; the voice sample's recorder on the browser's by default. */
  openRecorder?: () => Promise<RecorderDevice>
  /** How a take is sent; `sendVoiceSampleTake` by default. */
  send?: typeof sendVoiceSampleTake
}

export interface VoiceSampleFlowOptions {
  /** Whether the dialog is shown: a closed one holds no microphone and sends nothing. */
  open: boolean
  mode: VoiceSampleFlowMode
  /** The dialog closes: its X, *Done*, or a kept take handed back. */
  onClose: () => void
  /** A sample only this tab holds exists, or no longer does: for the page's leave guard. */
  onUnsavedChange?: (unsaved: boolean) => void
  deps?: VoiceSampleFlowDeps
}

/** The question asked before a sample is dropped: by closing, or by recording another. */
export interface VoiceDiscardQuestion {
  asking: 'close' | 'again' | null
  onKeep: () => void
  /** Drop it. For `again` this opens the microphone: it is called inside the tap. */
  onLose: () => void
}

export interface VoiceSampleFlow {
  importFile: (file: File) => RecordingFileProblem | null
  view: VoiceSampleView
  /** The counted speech and the level as they move: follow them with `useReadout`. */
  readout: ReadoutSource<VoiceReadout>
  /** The review's player on the recorded sample; null in every other phase. */
  player: VoicePlayer | null
  actions: VoiceSampleActions
  /** The backdrop and Escape: refused while the sample is on its way. */
  onDismiss: () => void
  /** Whether the dialog shows its close. */
  closable: boolean
  discard: VoiceDiscardQuestion
}

/** End this far below what the server takes: the recorder's last slice still has to fit. */
const STOP_AT_BYTES = MAX_VOICE_SAMPLE_BYTES - 256 * 1024

/**
 * How long the page's old news of a job that was run again is not believed: three of its polls.
 * After that an unchanged *failed* is the rerun's.
 */
const STALE_REPORT_MS = 12_000

type FlowDispatch = Dispatch<VoiceFlowEvent>

/** The dialog as it opens: saying so at once where this browser has no recorder. */
function firstState(openRecorder: VoiceSampleFlowDeps['openRecorder']): VoiceFlowState {
  if (openRecorder || canRecordHere()) return INITIAL_VOICE_FLOW
  return { phase: 'instructions', starting: false, problem: 'unsupported' }
}

interface SessionHandle {
  readout: ReadoutSource<VoiceReadout>
  start: () => void
  /** Count the speech although the room was found too loud. */
  goOn: () => void
  stop: () => void
  startOver: () => void
  /** Let the microphone go; nothing more is said. */
  release: () => void
}

/** The recording session for as long as the dialog is mounted, reporting to the machine. */
function useVoiceSession(
  openRecorder: () => Promise<RecorderDevice>,
  dispatch: FlowDispatch
): SessionHandle {
  const [readout] = useState(() => new ReadoutStore<VoiceReadout>(IDLE_VOICE_READOUT))
  const session = useRef<VoiceSampleSession | null>(null)

  useEffect(() => {
    const created = new VoiceSampleSession(
      {
        open: openRecorder,
        now: () => performance.now(),
        isoNow: () => new Date().toISOString(),
        maxBytes: STOP_AT_BYTES,
      },
      {
        onView: view => dispatch({ type: 'session', view }),
        onReadout: readout.set,
        onSampled: sample => dispatch({ type: 'sampled', intake: takeOfSample(sample) }),
      }
    )
    session.current = created
    return () => {
      created.dispose()
      session.current = null
    }
  }, [openRecorder, readout, dispatch])

  return useMemo(
    () => ({
      readout,
      // Opening the microphone answers through the session's view, also when it is refused.
      start: () => void session.current?.start(),
      goOn: () => session.current?.goOn(),
      stop: () => session.current?.stop(),
      startOver: () => void session.current?.startOver(),
      release: () => session.current?.dispose(),
    }),
    [readout]
  )
}

/** The share of the bytes sent, in the whole percents the screen shows. */
const sentShare = (sent: number, total: number): number =>
  total > 0 ? Math.floor((sent / total) * 100) / 100 : 0

interface Sender {
  /** Send the take for `target`: whom its first send named (`sendTarget`). */
  send: (take: VoiceSampleTake, target: VoiceSampleTarget) => void
  abort: () => void
}

/** Whom the page names for a sample kept now. */
const targetOf = ({ caseId, subjectRef, encounterRef }: VoiceUploadMode): VoiceSampleTarget => ({
  caseId,
  subjectRef,
  encounterRef,
})

/** One send of a take, each of its steps told to the machine; it never rejects. */
async function sendTake(
  sent: { take: VoiceSampleTake; target: VoiceSampleTarget },
  send: typeof sendVoiceSampleTake,
  signal: AbortSignal,
  dispatch: FlowDispatch
): Promise<VoiceSendOutcome> {
  try {
    return await send(sent.take, sent.target, {
      signal,
      onUploading: () => dispatch({ type: 'progress', progress: 0 }),
      onProgress: (bytes, total) =>
        dispatch({ type: 'progress', progress: sentShare(bytes, total) }),
    })
  } catch {
    // The send of the workspace never rejects; one handed in by a story might.
    return { kind: 'failed', problem: 'error' }
  }
}

/** The upload: one at a time, aborted when the dialog goes. */
function useVoiceSender(latest: RefObject<VoiceSampleFlowOptions>, dispatch: FlowDispatch): Sender {
  const sending = useRef<AbortController | null>(null)

  // The dialog goes: nothing more leaves the phone.
  useEffect(() => () => sending.current?.abort(), [])

  return useMemo(() => {
    const abort = () => {
      sending.current?.abort()
      sending.current = null
    }
    const send = async (take: VoiceSampleTake, target: VoiceSampleTarget) => {
      if (sending.current) return
      const controller = new AbortController()
      sending.current = controller
      dispatch({ type: 'send', target })
      const request = latest.current.deps?.send ?? sendVoiceSampleTake
      const outcome = await sendTake({ take, target }, request, controller.signal, dispatch)
      if (sending.current === controller) sending.current = null
      if (outcome.kind === 'cancelled' || controller.signal.aborted) return
      dispatch({ type: 'sent', outcome })
      const { mode } = latest.current
      if (outcome.kind === 'saved' && mode.kind === 'upload') mode.onStored?.(outcome.captureId)
    }
    return { send: (take, target) => void send(take, target), abort }
  }, [latest, dispatch])
}

/** The verdict as one object for as long as it says the same: the page hands over a new one at every poll. */
function useSameQuality(quality: VoiceSampleQuality | null): VoiceSampleQuality | null {
  const kept = useRef(quality)
  if (!sameQuality(kept.current, quality)) kept.current = quality
  return kept.current
}

/**
 * What the page knows of the stored capture — where its job stands, and the server's verdict on
 * the sample once there is one — told to the machine as it changes.
 */
function useReportedOutcome(
  state: VoiceFlowState,
  mode: VoiceSampleFlowMode,
  dispatch: FlowDispatch
): void {
  const captureId = storedCaptureId(state)
  const page = captureId && mode.kind === 'upload' ? mode : null
  const reported = voiceOutcomeOf(page?.captureStateOf?.(captureId ?? ''))
  const quality = useSameQuality(page?.captureQualityOf?.(captureId ?? '') ?? null)
  const current = useRef({ outcome: reported, quality })
  current.current = { outcome: reported, quality }

  useEffect(() => {
    if (captureId) dispatch({ type: 'reported', outcome: reported, quality })
  }, [captureId, reported, quality, dispatch])

  // A job that was run again: the page's old news of it is believed once it has had time to ask.
  const stale = state.phase === 'stored' && state.stale !== null
  useEffect(() => {
    if (!stale) return
    const believe = () => dispatch({ type: 'reported', ...current.current, fresh: true })
    const timer = setTimeout(believe, STALE_REPORT_MS)
    return () => clearTimeout(timer)
  }, [stale, dispatch])
}

/** A player as it stood, playing nothing: what a closed dialog draws of it while it goes. */
const stillPlayer = (player: VoicePlayer | null): VoicePlayer | null =>
  player && { ...player, playback: { ...player.playback, playing: false } }

/**
 * The recorded sample under the review's player, played from the tab's memory. Without a take —
 * a recording under way, a sample sent, a dialog closed — there is no object URL and no audio
 * element: the URL is revoked and the element let go, so nothing plays on.
 */
function useTakePlayer(state: VoiceFlowState): VoicePlayer | null {
  const take = state.phase === 'review' ? state.take : null
  const src = useObjectUrl(take?.audio.blob ?? null)
  const { playback, failed, toggle, seek } = useAudioPlayback(src, take?.audio.durationMs ?? 0)
  const player = useMemo(() => {
    if (!take) return null
    const audio: VoicePlayer['audio'] = failed ? 'unavailable' : src ? 'ready' : 'loading'
    return { audio, playback, actions: { onTogglePlay: toggle, onSeek: seek } }
  }, [take, src, failed, playback, toggle, seek])
  const closed = state.phase === 'closed'
  const last = useRef(player)
  if (!closed) last.current = player
  return closed ? stillPlayer(last.current) : player
}

/** Tell the page when a sample only this tab holds comes to be, and when it is no more. */
function useUnsavedReport(unsaved: boolean, latest: RefObject<VoiceSampleFlowOptions>): void {
  useEffect(() => {
    latest.current.onUnsavedChange?.(unsaved)
  }, [unsaved, latest])
  // The dialog goes with its sample: nothing is left to lose.
  useEffect(() => () => latest.current.onUnsavedChange?.(false), [latest])
}

type Handlers = Record<VoiceFlowOffer, () => void>

const ACTION_OF: Record<VoiceFlowOffer, keyof Omit<VoiceSampleActions, 'onClose'>> = {
  start: 'onStart',
  goOn: 'onGoOn',
  stop: 'onStop',
  startOver: 'onStartOver',
  recordAgain: 'onRecordAgain',
  save: 'onSave',
  retry: 'onRetry',
}

/**
 * The callbacks of what the phase offers: a control whose callback is missing is not drawn. A tap
 * is taken only where `counts` says it was aimed at this screen.
 */
function offeredActions(
  offers: readonly VoiceFlowOffer[],
  handlers: Handlers,
  taps: { onClose: () => void; counts: () => boolean }
): VoiceSampleActions {
  const offered = offers.map(offer => {
    const run = handlers[offer]
    const tap = () => {
      if (taps.counts()) run()
    }
    return [ACTION_OF[offer], tap] as const
  })
  return { onClose: taps.onClose, ...Object.fromEntries(offered) }
}

/** What the dialog draws now: a closed one goes on drawing it while it goes. */
interface Drawn {
  shown: VoiceSampleView
  offers: readonly VoiceFlowOffer[]
}

type Ending = Pick<FlowParts, 'dispatch' | 'session' | 'sender'>

/**
 * How one opening ends: the microphone is let go, nothing more is sent, and the machine holds
 * nothing — no take under the player, none to send or to bring back.
 */
function useEnding(drawn: Drawn, { dispatch, session, sender }: Ending): () => void {
  const last = useRef(drawn)
  last.current = drawn
  return useCallback(() => {
    session.release()
    sender.abort()
    dispatch({ type: 'closed', ...last.current })
  }, [dispatch, session, sender])
}

/**
 * Which taps count: none for a moment after the screen has changed, when the tap was on its way
 * to the button that stood there before (`VOICE_TAP_SETTLE_MS`).
 */
function useSettledTaps(screen: VoiceScreen): () => boolean {
  const arrival = useRef<ScreenArrival | null>(null)
  arrival.current = screenArrival(arrival.current, screen, performance.now())
  return useCallback(() => {
    const { current } = arrival
    return current !== null && tapCounts(current, performance.now())
  }, [])
}

interface FlowParts {
  state: VoiceFlowState
  dispatch: FlowDispatch
  session: SessionHandle
  sender: Sender
  latest: RefObject<VoiceSampleFlowOptions>
  /** End this opening (`useEnding`). */
  end: () => void
}

/** The taps the recording session takes as they are: record, go on in a loud room, stop. */
const sessionTaps = ({ start, goOn, stop }: SessionHandle) => ({ start, goOn, stop })

/** What each tap does, and the question asked before one of them drops a sample. */
function useVoiceActions({ state, dispatch, session, sender, latest, end }: FlowParts) {
  const [asking, setAsking] = useState<VoiceDiscardQuestion['asking']>(null)
  const take = heldTake(state)

  const close = () => {
    setAsking(null)
    end()
    latest.current.onClose()
  }
  const recordAgain = () => {
    setAsking(null)
    sender.abort()
    if (take?.fileName) dispatch({ type: 'chooseSource' })
    else {
      dispatch({ type: 'restart' })
      session.start()
    }
  }
  const send = () => {
    const { mode } = latest.current
    // A take that went before goes for whom it went then, not for whom the page names by now.
    if (take && mode.kind === 'upload') sender.send(take, sendTarget(state, targetOf(mode)))
  }
  /** Store the take with the subject, or hand it to whoever opened the dialog and close. */
  const keep = () => {
    const { mode } = latest.current
    if (!take || mode.kind === 'upload') return send()
    mode.onCollected(take)
    close()
  }
  const handlers: Handlers = {
    ...sessionTaps(session),
    startOver: () => {
      dispatch({ type: 'restart' })
      session.startOver()
    },
    recordAgain: () => (recordAgainAsks(state) ? setAsking('again') : recordAgain()),
    save: keep,
    retry: send,
  }
  const requestClose = () => {
    if (!isClosable(state)) return
    if (closeAsks(state)) setAsking('close')
    else close()
  }
  const discard: VoiceDiscardQuestion = {
    asking,
    onKeep: () => setAsking(null),
    onLose: asking === 'again' ? recordAgain : close,
  }
  return { handlers, requestClose, discard }
}

export function useVoiceSampleFlow(options: VoiceSampleFlowOptions): VoiceSampleFlow {
  const { open, mode, deps } = options
  const latest = useRef(options)
  latest.current = options
  const [state, dispatch] = useReducer(voiceFlowReducer, deps?.openRecorder, firstState)
  const session = useVoiceSession(deps?.openRecorder ?? openVoiceSampleRecorder, dispatch)
  const sender = useVoiceSender(latest, dispatch)
  useReportedOutcome(state, mode, dispatch)
  // The sample is safe only once the server has it: a phone that sleeps stops both.
  const busy = state.phase === 'room' || state.phase === 'recording' || state.phase === 'saving'
  const wakeLock = useScreenWakeLock(open && busy)
  const player = useTakePlayer(state)
  useUnsavedReport(open && hasUnsavedTake(state), latest)

  const view = toVoiceSampleView(state, wakeLock)
  const offers = voiceFlowOffers(state, mode.kind)
  const end = useEnding({ shown: view, offers }, { dispatch, session, sender })
  const counts = useSettledTaps(voiceScreenOf(view))

  // Closed from outside (the page went on): the opening ends as it does at the dialog's own close.
  useEffect(() => {
    if (!open) end()
  }, [open, end])

  const parts = { state, dispatch, session, sender, latest, end }
  const { handlers, requestClose, discard } = useVoiceActions(parts)
  return {
    importFile: file => {
      if (state.phase !== 'instructions' || state.starting) return null
      const intake = takeOfVoiceFile(file)
      if (intake.kind === 'refused') return intake.problem
      dispatch({ type: 'imported', take: intake.take })
      return null
    },
    view,
    readout: session.readout,
    player,
    actions: offeredActions(offers, handlers, { onClose: requestClose, counts }),
    onDismiss: requestClose,
    closable: isClosable(state),
    discard,
  }
}
